import { Contract, ProcessSpecification, Segment } from "@/types/contract";
import { Commodity, Process, Phase } from "@/types/contract";
import {
  BundleServiceId,
  BundleServiceIdFVE,
  BundleServiceIdHeatPump,
  DistributorIdGasnet,
  Tariff27,
} from "@/constants/supplyPoint";
import { PowerApplianceKey } from "@/constants/powerAppliances";
import { containsAny } from "@/utils/array";
import { EanPrefix, EicPrefix } from "@/constants/common";
import { UsageFormValues } from "./Usage/form";
import { DistributorIdCEZ } from "@/constants/supplier";
import { PowerEquipment, Product } from "@/types/enumerations";
import { TechnicalDataFormValues } from "./TechnicalData/types";
import { isHousehold } from "@/utils/customer";
import { getCircuitBreakerByIdentifier, getRevisionReport, showDoubleMeasurement } from "./TechnicalData/helpers";
import { isLDSEanOrEic } from "@/utils/supplyPoint";
import { InvoicesSendingTypeValues } from "@/constants/payments";
import { TechnicalDataGasFormValues } from "./TechnicalDataGas/form";
import { roundToTwoDecimals } from "@/utils/formatters";
import { CustomerTypeValues } from "@/constants/customer";
import { getProducts } from "@/screens/Tariff/Products/helpers";

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getYearConsumptionIsHighForGas(
  commodity: Commodity | undefined,
  unit: string,
  value: number,
  process: Process,
  customerType: CustomerTypeValues
) {
  if (process === Process.PS) {
    return (
      commodity === Commodity.Gas &&
      ((unit === "mwh" && value > 100) || (unit === "kwh" && value > 100000) || (unit === "m3" && value > 9478))
    );
  }

  if (process === Process.NEW_POINT && customerType !== CustomerTypeValues.Household) {
    return (
      commodity === Commodity.Gas &&
      ((unit === "mwh" && value > 630) || (unit === "kwh" && value > 630000) || (unit === "m3" && value > 59155))
    );
  }
  return (
    commodity === Commodity.Gas &&
    ((unit === "mwh" && value > 63) || (unit === "kwh" && value > 63000) || (unit === "m3" && value > 5971))
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getYearConsumptionIsHighForElectricity(
  commodity: Commodity | undefined,
  unit: string,
  value: number,
  process: Process,
  customerType: CustomerTypeValues
) {
  if (process === Process.PS && customerType !== CustomerTypeValues.Household) {
    return (
      commodity === Commodity.Electricity &&
      ((unit === "mwh" && value > 100) || (unit === "kwh" && value > 100000) || (unit === "m3" && value > 9478))
    );
  }

  if (process === Process.NEW_POINT && customerType !== CustomerTypeValues.Household) {
    return (
      commodity === Commodity.Electricity &&
      ((unit === "mwh" && value > 630) || (unit === "kwh" && value > 630000) || (unit === "m3" && value > 59155))
    );
  }

  return commodity === Commodity.Electricity && ((unit === "mwh" && value > 50) || (unit === "kwh" && value > 50000));
}

export function getYearConsumptionIsOverLimit(unit: string, value: number) {
  return (unit === "mwh" && value > 630) || (unit === "kwh" && value > 630000) || (unit === "m3" && value > 59715);
}

export function getYearConsumptionIsOverHundred(unit: string, filledYearConsumption: number) {
  return (
    (unit === "mwh" && filledYearConsumption > 100) ||
    (unit === "kwh" && filledYearConsumption > 100000) ||
    (unit === "m3" && filledYearConsumption > 9478)
  );
}

export const isPowerValueHightForHousehold = (
  value: number | null,
  yearConsumptionUnits: string,
  commodity: Commodity | undefined,
  process: Process,
  customerType: CustomerTypeValues
) => {
  if (!value || !commodity) {
    return false;
  }
  const yearConsumptionIsOverLimit = getYearConsumptionIsOverLimit(yearConsumptionUnits, value);
  const yearConsumptionIsHighForElectricity = getYearConsumptionIsHighForElectricity(
    commodity,
    yearConsumptionUnits,
    value,
    process,
    customerType
  );
  const yearConsumptionIsHighForGas = getYearConsumptionIsHighForGas(
    commodity,
    yearConsumptionUnits,
    value,
    process,
    customerType
  );
  return (yearConsumptionIsHighForElectricity || yearConsumptionIsHighForGas) && !yearConsumptionIsOverLimit;
};

export const getYearConsumptionValueProposal = (
  filledYearConsumption: number,
  yearConsumptionUnits: string,
  commodity: Commodity | undefined,
  process: Process,
  customerType: CustomerTypeValues
) => {
  const yearConsumptionIsOverHundred = getYearConsumptionIsOverHundred(yearConsumptionUnits, filledYearConsumption);

  if (yearConsumptionIsOverHundred) {
    const result = filledYearConsumption / 100;
    return roundToTwoDecimals(result);
  }

  if (commodity === Commodity.Electricity) {
    const yearConsumptionIsHighForElectricity = getYearConsumptionIsHighForElectricity(
      commodity,
      yearConsumptionUnits,
      filledYearConsumption,
      process,
      customerType
    );

    if (yearConsumptionIsHighForElectricity) {
      const result = filledYearConsumption / 10;
      return roundToTwoDecimals(result);
    }
  } else {
    const yearConsumptionIsHighForGas = getYearConsumptionIsHighForGas(
      commodity,
      yearConsumptionUnits,
      filledYearConsumption,
      process,
      customerType
    );

    if (yearConsumptionIsHighForGas) {
      const result = filledYearConsumption / 10;
      return roundToTwoDecimals(result);
    }
  }
};

export function getTotalYearConsumptionWithOptions(
  isSelfService: boolean,
  yearConsumptionNt: number | null,
  yearConsumptionNtUnits: string,
  yearConsumptionVt: number | null,
  yearConsumptionVtUnits: string,
  yearConsumption: number | null,
  yearConsumptionUnits: string,
  process: Process
): [number, string] {
  if (isSelfService && process !== Process.NEW_POINT && process !== Process.PS) {
    return getTotalYearConsumption(yearConsumptionNt, yearConsumptionNtUnits, yearConsumptionVt, yearConsumptionVtUnits);
  }

  return [yearConsumption ?? 0, yearConsumptionUnits];
}

export function getTotalYearConsumption(
  yearConsumptionNt: number | null,
  yearConsumptionNtUnits: string,
  yearConsumptionVt: number | null,
  yearConsumptionVtUnits: string
): [number, string] {
  if (yearConsumptionVt === null) {
    return [0, "mwh"];
  }
  if (yearConsumptionNt === null) {
    return [yearConsumptionVt, yearConsumptionVtUnits];
  }
  if (yearConsumptionNtUnits === "kwh" && yearConsumptionVtUnits === "kwh") {
    return [yearConsumptionNt + yearConsumptionVt, "kwh"];
  }
  const yearConsumptionNtMwh = yearConsumptionNtUnits === "kwh" ? yearConsumptionNt / 1000 : yearConsumptionNt;
  const yearConsumptionVtMwh = yearConsumptionVtUnits === "kwh" ? yearConsumptionVt / 1000 : yearConsumptionVt;
  return [yearConsumptionNtMwh + yearConsumptionVtMwh, "mwh"];
}

export const showAdditionalInformationBlock = (contract: Contract) => {
  const supplyPoint = contract.supplyPoint;
  const isGas = supplyPoint.commodity === Commodity.Gas;
  const distributionGasnet = supplyPoint.distribution.distributorId === DistributorIdGasnet;
  const isSopSelf = supplyPoint.sop?.self;
  const usageApartment = supplyPoint.sop?.supplyPointUsage === "R00" || supplyPoint.sop?.supplyPointUsage === "01";

  return (
    isGas &&
    distributionGasnet &&
    (contract.process === Process.PS ||
      contract.process === Process.CHANGED_WITH_HAND_OVER ||
      (contract.process === Process.NEW_POINT && (usageApartment || !isSopSelf)))
  );
};

export const getIsHighCircuitBreaker = (
  process: Process,
  circuitBreakerPhase: string | null,
  circuitBreakerValue: number | null
) => {
  if (!circuitBreakerPhase || !circuitBreakerValue) {
    return false;
  }
  return [Process.NEW_POINT, Process.ZDP].includes(process) && circuitBreakerPhase === "3" && circuitBreakerValue > 80;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const checkIfTariffAvailable = (tariff: string | null, hasHeatPump: boolean, powerAppliances: PowerApplianceKey[]) => {
  if (!tariff) {
    return true;
  }
  const tariffUpper = tariff.toUpperCase();

  if (["D01D", "D02D", "C01D", "C02D", "C03D", "D61D"].includes(tariffUpper)) {
    return !hasHeatPump;
  }

  if (["D25D", "C25D"].includes(tariffUpper)) {
    return !hasHeatPump && !powerAppliances.includes("SPB05") && powerAppliances.includes("SPB03");
  }

  if (["D26D", "C26D"].includes(tariffUpper)) {
    return !hasHeatPump && !powerAppliances.includes("SPB05") && powerAppliances.includes("SPB04");
  }

  if (tariffUpper === "D27D") {
    return !hasHeatPump && !powerAppliances.includes("SPB05") && powerAppliances.includes("SPB18");
  }

  if (tariffUpper === "C27D") {
    return !hasHeatPump && powerAppliances.includes("SPB18") && !containsAny(powerAppliances, ["SPB03", "SPB04", "SPB05"]);
  }

  if (tariffUpper === "C45D") {
    return !hasHeatPump && powerAppliances.includes("SPB05");
  }

  if (tariffUpper === "C46D") {
    return !hasHeatPump && powerAppliances.includes("SPB05");
  }

  if (tariffUpper === "D57D") {
    return hasHeatPump || powerAppliances.includes("SPB05");
  }

  if (tariffUpper === "C56D") {
    return hasHeatPump;
  }

  return false;
};

export const checkIfSopRequired = (sopSelf: boolean | null, distributorId: string, code: string | null, commodity: Commodity) => {
  if (sopSelf === true) {
    return false;
  }

  if (!code) {
    return ["ED-PRE", "GD-PPD"].includes(distributorId);
  }

  //regex excludes 3 as it is ean for PRE, so it validates eans for LDS and PRE eans
  const eanRegex = new RegExp(/^(?!859182400[1245678])\d+$/);
  const eicRegex = new RegExp(/^(?!27ZG(200|300|400|500|600|700|900)).*$/);

  if (commodity === Commodity.Electricity) {
    const codeWithPrefix = `${EanPrefix}${code}`;
    return eanRegex.test(codeWithPrefix);
  }

  const codeWithPrefix = `${EicPrefix}${code}`;
  return eicRegex.test(codeWithPrefix);
};

export const getUsageCoefficient = (values: UsageFormValues) => {
  const usageCoefficient = values.usage === "no" ? 0.5 : 1;
  const fveCoefficient = values.fve ? 0.7 : 1;
  return usageCoefficient * fveCoefficient;
};

export const showCEZOwnershipAttachment = (contract: Contract) => {
  return contract.supplyPoint.distribution.distributorId === DistributorIdCEZ && contract.process === Process.PS;
};

const isLocalDistribution = (contract: Contract) => {
  const gridLevelType = contract.supplyPoint.distribution.gridLevelType;
  return gridLevelType === "0003" || gridLevelType === "0004";
};

export const showOwnershipAttachment = (contract: Contract) => {
  return contract.supplyPoint.commodity === Commodity.Gas && isLocalDistribution(contract) && contract.process === Process.PS;
};

export const showOwnershipOfElectricCarAttachment = (contract: Contract) => {
  return Tariff27.includes(contract.supplyPoint.tariff ?? "") && contract.process === Process.PS;
};

export const showBoilerRoomAttachment = (contract: Contract, values: TechnicalDataGasFormValues) => {
  return (
    (contract.process === Process.PS && contract.processSpecification) === ProcessSpecification.ONLINE &&
    !isHousehold(contract.customer) &&
    values.exceptionGasTax === true
  );
};

const cezTariffs = ["D25D", "D26D", "D27D", "D57D", "C25D", "C26D", "C27D", "C45D", "C46D", "C57D"];
const preTariffs = ["D25D", "D26D", "D27D", "D57D", "C25D", "C26D", "C27D", "C45D", "C46D", "C57D"];
const ldsTariffs = ["D25D", "D26D", "D27D", "D57D", "C25D", "C26D", "C27D", "C45D", "C46D", "C57D"];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const showAttachmentC0P = (contract: Contract) => {
  const process = contract.process;
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const supplyPoint = contract.supplyPoint;
  const isSopSelf = supplyPoint.sop!.self === true;
  const distributorId = supplyPoint.distribution.distributorId?.toString() ?? "";

  if (process === Process.ZDP) {
    if (!isSopSelf) {
      return false;
    }
    if (
      (supplyPoint.circuitBreakerPhase !== supplyPoint.currentCircuitBreakerPhase ||
        supplyPoint.circuitBreakerValue !== supplyPoint.currentCircuitBreakerValue) &&
      (["ED-PRE", "ED-CEZ"].includes(distributorId) || isLDSEanOrEic(contract, supplyPoint.code))
    ) {
      return true;
    }
    return (
      supplyPoint.currentTariff !== supplyPoint.tariff &&
      ((distributorId === "ED-CEZ" && cezTariffs.includes(supplyPoint.tariff ?? "")) ||
        (distributorId === "ED-PRE" && preTariffs.includes(supplyPoint.tariff ?? "")) ||
        (isLDSEanOrEic(contract, supplyPoint.code) && ldsTariffs.includes(supplyPoint.tariff ?? "")))
    );
  }
  return (
    (process === Process.NEW_POINT && !contractFromRequest && isSopSelf) ||
    (process === Process.CHANGED_WITH_HAND_OVER && isSopSelf)
  );
};

export const showAttachmentCNP = (contract: Contract) => {
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;

  if (contract.process === Process.NEW_POINT) {
    return Tariff27.includes(contract.supplyPoint.tariff ?? "") && !contractFromRequest;
  }
  if (contract.process === Process.ZDP) {
    return (
      contract.supplyPoint.currentTariffId !== contract.supplyPoint.tariffId &&
      Tariff27.includes(contract.supplyPoint.tariff ?? "")
    );
  }
  return false;
};

export const showAttachmentD2P = (contract: Contract) => {
  return (
    (contract.supplyPoint.circuitBreakerValue != contract.supplyPoint.currentCircuitBreakerValue &&
      contract.supplyPoint.circuitBreakerValue! > 80 &&
      contract.process === Process.ZDP) ||
    (contract.process === Process.NEW_POINT && contract.supplyPoint.circuitBreakerValue! > 80)
  );
};

export const showAttachmentCOP = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const tariffRegex = new RegExp(/^d/i);

  if (
    contract.process === Process.ZDP &&
    contract.segment === Segment.B2B &&
    formValues.tariff !== contract.supplyPoint.currentTariff &&
    tariffRegex.test(formValues.tariff ?? "")
  ) {
    return true;
  }
  return contract.process === Process.NEW_POINT && contract.segment === Segment.B2B && tariffRegex.test(formValues.tariff ?? "");
};

export const showAttachmentL3P = (contract: Contract, powerAppliances: PowerEquipment[]) => {
  const sop = contract.supplyPoint.sop!;
  const heatPump = contract.supplyPoint.sop?.heatPump[0];
  const getPowerApplianceId = (value: PowerApplianceKey) => powerAppliances.find(pa => pa.value === value)?.id ?? null;
  const getConsumption = (value: PowerApplianceKey, defaultValue: number | null) =>
    sop.powerAppliance.find(pa => pa.powerApplianceId === getPowerApplianceId(value))?.consumption ?? defaultValue;
  const consumption =
    getConsumption("SPB03", 0)! + getConsumption("SPB04", 0)! + getConsumption("SPB05", 0)! + (heatPump?.ratedPower ?? 0);

  if (contract.supplyPoint.currentTariffId !== contract.supplyPoint.tariffId && contract.process === Process.ZDP) {
    if (["D26D", "C26D"].includes(contract.supplyPoint.tariff ?? "")) {
      return consumption < contract.supplyPoint.circuitBreakerPhase! * contract.supplyPoint.circuitBreakerValue! * 0.1265;
    }
    if (["C45D", "C46D", "C56D", "D57D"].includes(contract.supplyPoint.tariff ?? "")) {
      return consumption < contract.supplyPoint.circuitBreakerPhase! * contract.supplyPoint.circuitBreakerValue! * 0.092;
    }
  }

  return false;
};

export const showAttachmentC7P = (contract: Contract) => {
  const isElectricity = contract.supplyPoint.commodity === Commodity.Electricity;
  const isGas = contract.supplyPoint.commodity === Commodity.Gas;
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const distributorId = contract.supplyPoint.distribution.distributorId?.toString() ?? "";
  return (
    contract.process === Process.NEW_POINT &&
    !contractFromRequest &&
    ((isElectricity && distributorId !== "ED-EON") || isGas) &&
    contract.segment === Segment.B2C
  );
};

const showCLPforZDPforPRE = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const currentTariff = contract.supplyPoint.currentTariff ?? "";
  const hasHeatPump = contract.supplyPoint.sop!.heatPump.length > 0;
  const formTariff = formValues.tariff ?? "";

  if (contract.supplyPoint.sop?.self === false) {
    return false;
  }
  if (["D57D"].includes(formTariff) && currentTariff === "D45D" && !hasHeatPump) {
    return false;
  }
  if (["D26D", "C56D", "D25D", "D57D"].includes(formTariff)) {
    return true;
  }
  if (["D27D", "C27D"].includes(formTariff) && contract.supplyPoint.measurementType === "B") {
    return true;
  }
  if (["D61D"].includes(formTariff) && ["D01D", "D02D"].includes(currentTariff)) {
    return true;
  }
  if (["C25D", "C26D"].includes(formTariff) && !["C35D", "C45D", "C46D", "C55D", "C56D"].includes(currentTariff)) {
    return true;
  }
  if (["C45D"].includes(formTariff) && !["C45D", "C46D", "C55D", "C56D"].includes(currentTariff)) {
    return true;
  }
  return !!(["C46D"].includes(formTariff) && !["C56D"].includes(currentTariff));
};

const showCLPforZDPforLDS = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const currentTariff = contract.supplyPoint.currentTariff ?? "";
  const hasHeatPump = contract.supplyPoint.sop!.heatPump.length > 0;
  const formTariff = formValues.tariff ?? "";

  if (contract.supplyPoint.sop?.self === false) {
    return false;
  }
  if (["D57D"].includes(formTariff) && currentTariff === "D45D" && !hasHeatPump) {
    return false;
  }
  if (["D25D", "D26D", "D57D", "C56D"].includes(formTariff)) {
    return true;
  }
  if (["D27D", "C27D"].includes(formTariff) && contract.supplyPoint.measurementType === "B") {
    return true;
  }
  if (formTariff === "D61D" && ["D01D", "D02D"].includes(currentTariff)) {
    return true;
  }
  if (["C25D", "C26D"].includes(formTariff) && ["C35D", "C45D", "C46D", "C55D", "C56D"].includes(currentTariff)) {
    return true;
  }
  if (formTariff === "C45D" && ["C45D", "C46D", "C55D", "C56D"].includes(currentTariff)) {
    return true;
  }
  return formTariff === "C46D" && currentTariff === "C56D";
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const showCLPforZDPforEGD = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const currentTariff = contract.supplyPoint.currentTariff ?? "";
  const hasHeatPump = contract.supplyPoint.sop!.heatPump.length > 0;
  if (contract.supplyPoint.sop?.self !== false) {
    const formTariff = formValues.tariff ?? "";
    if (["C26D", "C27D", "C46D", "D26D", "C56D"].includes(formTariff)) {
      return true;
    }
    if (["D25D"].includes(formTariff) && currentTariff !== "D26D") {
      return true;
    }
    if (["D57D"].includes(formTariff) && !["D45D", "D56D"].includes(currentTariff)) {
      return true;
    }
    if (["D57D"].includes(formTariff) && currentTariff === "D45D" && hasHeatPump) {
      return true;
    }
    if (["C25D"].includes(formTariff) && currentTariff !== "C26D") {
      return true;
    }
    return !!(["C45D"].includes(formTariff) && currentTariff !== "C46D");
  }
  return false;
};

const showCLPforZDPforCEZ = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const currentTariff = contract.supplyPoint.currentTariff ?? "";
  const hasHeatPump = contract.supplyPoint.sop!.heatPump.length > 0;
  const formTariff = formValues.tariff ?? "";
  if (contract.supplyPoint.sop?.self === false) {
    return false;
  }
  if (["D25D", "D27D", "C27D", "D26D", "C56D"].includes(formTariff)) {
    return true;
  }
  if (["D57D"].includes(formTariff) && !["D45D", "D56D"].includes(currentTariff)) {
    return true;
  }
  if (["D57D"].includes(formTariff) && currentTariff === "D45D" && hasHeatPump) {
    return true;
  }
  if (["D61D"].includes(formTariff) && ["D01D", "D02D"].includes(currentTariff)) {
    return true;
  }
  if (["C25D", "C26D"].includes(formTariff) && !["C35D", "C45D", "C46D", "C55D", "C56D"].includes(currentTariff)) {
    return true;
  }
  if (["C45D"].includes(formTariff) && !["C45D", "C46D", "C55D", "C56D"].includes(currentTariff)) {
    return true;
  }
  return !!(["C46D"].includes(formTariff) && currentTariff !== "C56D");
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const showCLPforZDP = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const distributorId = contract.supplyPoint.distribution.distributorId?.toString() ?? "";
  const formCircuitBreaker = getCircuitBreakerByIdentifier(formValues.circuitBreaker ?? "");
  const currentCircuitBreakerPhase = contract.supplyPoint.currentCircuitBreakerPhase;
  const currentCircuitBreakerValue = contract.supplyPoint.currentCircuitBreakerValue;
  const isSopSelf = contract.supplyPoint.sop!.self === true;
  const isCircuitBreakerChanged = contract.supplyPoint.changeCircuitBreaker === true && formValues.circuitBreaker;

  if (isSopSelf && isCircuitBreakerChanged) {
    if (
      (currentCircuitBreakerPhase !== formCircuitBreaker!.circuitBreakerPhase ||
        currentCircuitBreakerValue !== formCircuitBreaker!.circuitBreakerValue) &&
      distributorId === "ED-EON"
    ) {
      return true;
    }

    if (
      currentCircuitBreakerPhase &&
      currentCircuitBreakerValue &&
      formCircuitBreaker!.circuitBreakerPhase &&
      formCircuitBreaker!.circuitBreakerValue &&
      (["ED-CEZ", "ED-PRE"].includes(distributorId) || isLDSEanOrEic(contract, formValues.code))
    ) {
      if (
        currentCircuitBreakerPhase < formCircuitBreaker!.circuitBreakerPhase ||
        currentCircuitBreakerValue < formCircuitBreaker!.circuitBreakerValue
      ) {
        return true;
      }
      return currentCircuitBreakerPhase > formCircuitBreaker!.circuitBreakerPhase;
    }
  }

  if (contract.supplyPoint.changeTariff === true && contract.supplyPoint.currentTariff !== formValues.tariff) {
    if (distributorId === "ED-EON") {
      return showCLPforZDPforEGD(contract, formValues);
    }
    if (distributorId === "ED-CEZ") {
      return showCLPforZDPforCEZ(contract, formValues);
    }
    if (distributorId === "ED-PRE") {
      return showCLPforZDPforPRE(contract, formValues);
    }
    if (isLDSEanOrEic(contract, formValues.code)) {
      return showCLPforZDPforLDS(contract, formValues);
    }
    return false;
  }
  return false;
};
export const showAttachmentCLP = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const distributorId = contract.supplyPoint.distribution.distributorId?.toString() ?? "";
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const isSopSelf = contract.supplyPoint.sop!.self === true;
  const isElectricity = contract.supplyPoint.commodity === Commodity.Electricity;

  if (contract.process === Process.NEW_POINT) {
    return (
      isSopSelf &&
      !contractFromRequest &&
      ((isElectricity && ["ED-EON", "ED-CEZ", "GD-PPD"].includes(distributorId)) || ["ED-CEZ", "GD-PPD"].includes(distributorId))
    );
  }

  if (contract.process === Process.ZDP) {
    return showCLPforZDP(contract, formValues);
  }

  return false;
};

export const showHeatPumpDetail = (contract: Contract, values: TechnicalDataFormValues) => {
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const isElectricity = contract.supplyPoint.commodity === Commodity.Electricity;
  const heatPump = contract.supplyPoint.sop?.heatPump[0];
  const revisionReport = getRevisionReport(contract, values);
  if (contract.process === Process.ZDP && isElectricity && !!heatPump && contract.supplyPoint.sop?.self !== true) {
    if (contract.supplyPoint.distribution.distributorId === DistributorIdCEZ) {
      return true;
    }
    return revisionReport === "K";
  }
  return contract.process === Process.NEW_POINT && isElectricity && !contractFromRequest && !!heatPump;
};

export const showHeatPumpData = (contract: Contract, products: Product[]) => {
  const isElectricity = contract.supplyPoint.commodity === Commodity.Electricity;
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const isTariffWithBundleServiceForHeatPump =
    products
      .find(product => product.id === contract.contractDetails!.productId)
      ?.bundleServices.includes(BundleServiceIdHeatPump) === true;

  if (contract.process === Process.ZDP) {
    return false;
  }

  if (isElectricity && contract.process === Process.RECONTRACTING && isTariffWithBundleServiceForHeatPump) {
    return true;
  }
  return isElectricity && contract.process !== Process.NEW_POINT && !contractFromRequest && isTariffWithBundleServiceForHeatPump;
};

export const showFVEData = (contract: Contract, products: Product[]) => {
  const isElectricity = contract.supplyPoint.commodity === Commodity.Electricity;
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const isTariffWithBundleServiceForFVE =
    products.find(product => product.id === contract.contractDetails!.productId)?.bundleServices.includes(BundleServiceIdFVE) ===
    true;

  if (contract.process === Process.ZDP) {
    return false;
  }

  if (isElectricity && contract.process === Process.RECONTRACTING && isTariffWithBundleServiceForFVE) {
    return true;
  }
  return isElectricity && contract.process !== Process.NEW_POINT && !contractFromRequest && isTariffWithBundleServiceForFVE;
};

export const showKettleData = (contract: Contract, products: Product[]) => {
  const isGas = contract.supplyPoint.commodity === Commodity.Gas;
  const contractFromRequest = contract.phase === Phase.CONTRACT_FROM_REQUEST;
  const isTariffWithBundleService =
    products.find(product => product.id === contract.contractDetails!.productId)?.bundleServices.includes(BundleServiceId) ===
    true;
  if (contract.process === Process.PS) {
    return false;
  }

  return (
    isGas &&
    ((contract.process !== Process.NEW_POINT && !contractFromRequest && isTariffWithBundleService) ||
      (contract.process === Process.RECONTRACTING && isTariffWithBundleService))
  );
};

export const showRegulationLevelsBlocks = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const formCircuitBreaker = getCircuitBreakerByIdentifier(formValues.circuitBreaker ?? "");

  return (
    contract.process === Process.NEW_POINT &&
    contract.segment === Segment.B2B &&
    ((formCircuitBreaker?.circuitBreakerValue ?? 0) >= 199 || (formValues.circuitBreakerValue ?? 0) >= 199)
  );
};

export const showAuditReportAttachment = (contract: Contract) => {
  const currentTariff = contract.supplyPoint.currentTariff ?? "";
  const tariff = contract.supplyPoint.tariff ?? "";
  const distributorId = contract.supplyPoint.distribution.distributorId?.toString() ?? "";
  if (contract.oldCustomer) {
    return (
      isHousehold(contract.oldCustomer) &&
      !isHousehold(contract.customer) &&
      distributorId === "ED-EON" &&
      contract.supplyPoint.codeSecondary !== "" &&
      ((currentTariff === "D45D" && tariff === "C46D") ||
        (currentTariff === "D56D" && tariff === "C56D") ||
        (currentTariff === "D57D" && tariff === "C46D"))
    );
  }
  return false;
};

export const checkIfCodeSecondaryRequired = (contract: Contract, formValues: TechnicalDataFormValues) => {
  return showDoubleMeasurement(contract, formValues) && formValues.doubleMeasurement === "false" && !formValues.codeSecondary;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const showStatusData = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const supplyPoint = contract.supplyPoint;
  const currentTariff = supplyPoint.currentTariff;
  const tariff = formValues.tariff;
  const formCircuitBreaker = getCircuitBreakerByIdentifier(formValues.circuitBreaker ?? "");
  const isChangeInCircuitBreaker =
    supplyPoint.changeCircuitBreaker === true &&
    formCircuitBreaker &&
    (supplyPoint.currentCircuitBreakerPhase !== formCircuitBreaker.circuitBreakerPhase ||
      supplyPoint.currentCircuitBreakerValue !== formCircuitBreaker.circuitBreakerValue);

  if (
    contract.process === Process.ZDP &&
    supplyPoint.measurementType === "C" &&
    [4, null].includes(supplyPoint.measurementCategory) &&
    !isChangeInCircuitBreaker
  ) {
    if ((currentTariff === "D01D" && tariff === "D02D") || (currentTariff === "D02D" && tariff === "D01D")) {
      return true;
    }
    if ((currentTariff === "C01D" && tariff === "C02D") || (currentTariff === "C02D" && tariff === "C01D")) {
      return true;
    }
    if ((currentTariff === "C01D" && tariff === "C03D") || (currentTariff === "C03D" && tariff === "C01D")) {
      return true;
    }
    return !!((currentTariff === "C02D" && tariff === "C03D") || (currentTariff === "C03D" && tariff === "C02D"));
  } else {
    return false;
  }
};

export const getSendingInvoiceType = (contract: Contract) => {
  if (
    ["S1", "S2", "S3", "S4", "R1", "R2", "R3", "MS", "VRB", "ZDP"].includes(contract.process) &&
    contract.selfService === true
  ) {
    return InvoicesSendingTypeValues.Mail;
  }
  return contract.paymentInfo!.sendingInvoicesType;
};

export const showGasApplianceData = (contract: Contract, formValues: TechnicalDataGasFormValues) => {
  return (
    contract.process === Process.NEW_POINT &&
    contract.segment === Segment.B2B &&
    contract.supplyPoint.commodity === Commodity.Gas &&
    formValues.sop?.tdS04 === true
  );
};

export const showMeasurementTransformers = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const formCircuitBreaker = getCircuitBreakerByIdentifier(formValues.circuitBreaker ?? "");

  return (
    (contract.process === Process.NEW_POINT || contract.process === Process.ZDP) &&
    ((formCircuitBreaker?.circuitBreakerValue ?? 0) > 80 || (formValues.circuitBreakerValue ?? 0) > 80)
  );
};

// Collects the ids of every product that could be shown on /tariff, so deposit
// recommendations can be prefetched for all of them.
export const getProductIds = (contract: Contract, productEnums: Product[]): string[] => {
  const { products, previousCustomerProducts } = getProducts(contract, productEnums, false);
  const ids = [...products, ...previousCustomerProducts].map(p => String(p.id));
  return Array.from(new Set(ids));
};
